// Quiet auto-update (KAN-886 / RD-OPS-007). Given a newer deploy that
// useUpdateAvailable found, reload onto it at a SAFE moment instead of asking:
//   A. WAKE-UP — the app was in the background >= 5 min and just came back: for
//      15s (after a 2s settle for the token refresh), retry the safety gate every
//      500ms and reload the first time it passes; re-checked just before reload.
//   B. TAB SWITCH — while a newer version is pending, the decision is made AT
//      THE TAP on a top-level nav control (old tab, its loads settled): if the
//      full gate passes the tap "arms" for 2s, and the tab change that follows
//      reloads onto the new tab without re-asking (the new tab's own RPCs are
//      POSTs and would otherwise read as in-flight writes). A tab change with no
//      fresh arm (back button, in-page link) retries the normal gate every
//      500ms for 5s.
// "Safe" is `canReloadSilently` (src/utils/app/silentUpdateGate.js) — it fails
// closed. Loop guard: one silent attempt per version per 10 min (sessionStorage);
// a second one means the reload did not land the new build, so the visible
// prompt is due at once. No router import on purpose (a router in a leaf breaks
// Jest trees): the tab key is read from location.pathname.

import { useCallback, useEffect, useRef, useState } from 'react';
import { canReloadSilently } from '../utils/app/silentUpdateGate';
import { isTopNavClickTarget, topNavTargetKey } from '../utils/app/topNavTarget';
import { hasPendingWrites } from '../utils/app/pendingWrites';

export const RESUME_MIN_HIDDEN_MS = 5 * 60_000;
export const RESUME_WINDOW_MS = 15_000;
export const PROMPT_BACKSTOP_MS = 24 * 60 * 60_000;
export const SILENT_RETRY_GUARD_MS = 10 * 60_000;
const POLL_MS = 500;
export const ARM_FRESH_MS = 2000;
export const TAB_RETRY_WINDOW_MS = 5000;
// Wake-up: let the SDK's own token refresh go out (and be counted) before trying.
export const RESUME_SETTLE_MS = 2000;
const COMMIT_DELAY_MS = 50; // let the splash commit before the reload starts
const ATTEMPT_KEY = 'crewvex.update.silentAttempt';
const REQUIRED_RELOAD_KEY = 'crewvex.update.requiredReloadAt';
export const REQUIRED_RELOAD_COOLDOWN_MS = 60_000;

function recentRequiredReload() {
  try {
    const at = Number(window.sessionStorage.getItem(REQUIRED_RELOAD_KEY));
    return Number.isFinite(at) && at > 0 && Date.now() - at < REQUIRED_RELOAD_COOLDOWN_MS;
  } catch (_) {
    return false;
  }
}

function markRequiredReload() {
  try {
    window.sessionStorage.setItem(REQUIRED_RELOAD_KEY, String(Date.now()));
  } catch (_) {
    /* storage unavailable — once-per-page still holds */
  }
}

/** First path segment after /app/ or /platform/ (e.g. /app/route/season → 'route'). */
export function topLevelTabKey(pathname) {
  const m = /^\/(?:app|platform)\/([^/?#]+)/.exec(String(pathname || ''));
  return m ? m[1] : null;
}

function readAttempt() {
  try {
    return JSON.parse(window.sessionStorage.getItem(ATTEMPT_KEY) || 'null');
  } catch (_) {
    return null;
  }
}

function writeAttempt(version, at) {
  try {
    window.sessionStorage.setItem(ATTEMPT_KEY, JSON.stringify({ version, at }));
  } catch (_) {
    /* storage unavailable — the guard just has no record */
  }
}

/**
 * @param {{ latestVersion: string|null, performUpdate: () => Promise<void> }} args
 * @returns {{ silentReloading: boolean, guardTripped: boolean, reloadQuietlyNow: () => boolean }}
 */
export function useSilentAppUpdate({ latestVersion, performUpdate }) {
  const [silentReloading, setSilentReloading] = useState(false);
  const [guardTripped, setGuardTripped] = useState(false);
  const [windowOpen, setWindowOpen] = useState(false);
  const versionRef = useRef(latestVersion);
  versionRef.current = latestVersion;
  const performRef = useRef(performUpdate);
  performRef.current = performUpdate;
  const reloadingRef = useRef(false);
  const hiddenAtRef = useRef(null);

  const requiredFiredRef = useRef(false);

  // `force` (KAN-887, a REQUIRED update): needs no pending version, skips the
  // 10-min loop guard, and fires at most once per page (and once a minute per
  // session, so a stale cached copy shows the card instead of looping).
  const attempt = useCallback((force = false, preApproved = false) => {
    const version = force ? versionRef.current || 'required' : versionRef.current;
    if (!version || reloadingRef.current) return false;
    if (force && requiredFiredRef.current) return false;
    // A required reload that already ran this session and still landed an old
    // copy (a stale cache) must not loop: the card takes over instead.
    if (force && recentRequiredReload()) return false;
    if (!preApproved && !canReloadSilently().ok) return false;
    const now = Date.now();
    const prev = force ? null : readAttempt();
    if (prev && prev.version === version && now - Number(prev.at) < SILENT_RETRY_GUARD_MS) {
      setGuardTripped(true);
      return false;
    }
    reloadingRef.current = true;
    setSilentReloading(true);
    setTimeout(() => {
      // Re-check at the last moment: a write that began in the gap wins.
      // A tap-approved reload still never cuts off a save that started after the
      // tap (a save-on-leave, an autosave on blur) — security review, KAN-897.
      if (preApproved ? hasPendingWrites() : !canReloadSilently().ok) {
        reloadingRef.current = false;
        setSilentReloading(false);
        return;
      }
      // Record the attempt only once the reload really goes ahead.
      if (force) {
        requiredFiredRef.current = true;
        markRequiredReload();
      }
      writeAttempt(version, Date.now());
      void performRef.current();
    }, COMMIT_DELAY_MS);
    return true;
  }, []);

  // A tripped guard belongs to the version it tripped for — a newer one starts clean.
  useEffect(() => { setGuardTripped(false); }, [latestVersion]);

  // Path A — wake-up window.
  useEffect(() => {
    let closeTimer = null;
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAtRef.current = Date.now();
        return;
      }
      const hiddenAt = hiddenAtRef.current;
      hiddenAtRef.current = null;
      if (hiddenAt == null || Date.now() - hiddenAt < RESUME_MIN_HIDDEN_MS) return;
      setWindowOpen(true);
      clearTimeout(closeTimer);
      closeTimer = setTimeout(() => setWindowOpen(false), RESUME_WINDOW_MS);
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearTimeout(closeTimer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  useEffect(() => {
    if (!windowOpen || !latestVersion) return undefined;
    let id = null;
    const start = setTimeout(() => {
      attempt();
      id = setInterval(() => attempt(), POLL_MS);
    }, RESUME_SETTLE_MS);
    return () => { clearTimeout(start); clearInterval(id); };
  }, [windowOpen, latestVersion, attempt]);

  // Path B — top-level tab change while a newer version is pending.
  useEffect(() => {
    if (!latestVersion) return undefined;
    let lastKey = topLevelTabKey(window.location.pathname);
    let arm = null; // { at, key } — the FULL gate passed at the tap
    let retryId = null;
    let retryUntil = 0;
    const stopRetry = () => { clearInterval(retryId); retryId = null; };

    const onClick = (e) => {
      arm = null;
      if (reloadingRef.current || !isTopNavClickTarget(e.target)) return;
      // Re-tapping the tab you are on is not a tab change: never arm on it.
      if (topNavTargetKey(e.target) === lastKey) return;
      if (canReloadSilently().ok) arm = { at: Date.now(), key: lastKey };
    };

    const onChange = () => {
      const key = topLevelTabKey(window.location.pathname);
      if (key === lastKey) return;
      lastKey = key;
      stopRetry();
      const armed = arm;
      arm = null;
      const tapApproved = armed && key !== armed.key && Date.now() - armed.at <= ARM_FRESH_MS;
      // Either way a retry window follows: a tap-approved reload can still step
      // aside for a save that started after the tap, and then retries here.
      if (tapApproved ? attempt(false, true) : attempt()) {
        if (!tapApproved) return;
      }
      retryUntil = Date.now() + TAB_RETRY_WINDOW_MS;
      retryId = setInterval(() => {
        if (Date.now() > retryUntil) { stopRetry(); return; }
        if (reloadingRef.current) return; // a reload is committing — wait, don't stop
        attempt();
      }, POLL_MS);
    };

    document.addEventListener('click', onClick, true);
    const id = setInterval(onChange, POLL_MS);
    window.addEventListener('popstate', onChange);
    return () => {
      stopRetry();
      clearInterval(id);
      document.removeEventListener('click', onClick, true);
      window.removeEventListener('popstate', onChange);
    };
  }, [latestVersion, attempt]);

  // For the REQUIRED-update flow: the same quiet path, loop guard ignored.
  const reloadQuietlyNow = useCallback(() => attempt(true), [attempt]);

  return { silentReloading, guardTripped, reloadQuietlyNow };
}
